/* eslint-disable import/no-unresolved, import/no-extraneous-dependencies, no-return-await, no-void */
import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useNucleusSession from '@omniverse/auth/react/hooks/NucleusSession';
import {
  OmniverseHeader,
  LoaderError,
  SessionReportDialog,
  StreamLoader,
  StreamError,
} from '../components';
import { useOmniverseConfig } from '../hooks/useConfig';
import useStream from '../hooks/useStream';
import useStreamEndNotification from '../hooks/useStreamEndNotification';
import useStreamStart from '../hooks/useStreamStart';
import {
  AuthenticationType,
  getStreamingApp,
  StreamingApp,
} from '../state/Apps';

/**
 * Loads the information about the application with the specified ID
 * and specified session and starts the stream.
 */
export default function OmniverseAppStream() {
  const { appId = '', sessionId = '' } = useParams<{
    appId: string;
    sessionId: string;
  }>();
  const config = useOmniverseConfig();
  const nucleus = useNucleusSession();

  const { isLoading, data, isError, error } = useQuery({
    queryKey: ['streaming-app', appId],
    queryFn: async () =>
      await getStreamingApp({
        appId,
        config,
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <OmniverseHeader />
        <Box sx={{ position: 'relative' }}>
          <StreamLoader />
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <OmniverseHeader />
        <LoaderError title={'Failed to load the stream'}>
          {error.toString()}
        </LoaderError>
      </Box>
    );
  }

  if (data) {
    if (data.authType === AuthenticationType.nucleus) {
      if (!nucleus.established) {
        return (
          <Navigate
            to={`/omniverse/nucleus/authenticate?redirectAfter=/omniverse/app/${appId}/sessions/${sessionId}`}
          />
        );
      }

      if (!nucleus.accessToken) {
        return (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}
          >
            <OmniverseHeader />
            <Box sx={{ position: 'relative' }}>
              <StreamLoader />
            </Box>
          </Box>
        );
      }
    }
    return <AppStreamSession app={data} sessionId={sessionId} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <OmniverseHeader />
      <LoaderError title={'Failed to load the stream'}>
        Application not found.
      </LoaderError>
    </Box>
  );
}

interface StreamSessionProps {
  app: StreamingApp;
  sessionId: string;
}

function AppStreamSession({ app, sessionId }: StreamSessionProps) {
  const navigate = useNavigate();
  const stream = useStream({ app, sessionId });
  const streamStart = useStreamStart(app.id);
  useStreamEndNotification(sessionId);

  const [fullScreen, setFullScreen] = useState(false);
  const videoElement = useRef<HTMLVideoElement>(null);
  const [reportOpened, setReportOpened] = useState(false);

  const toggleReport = () => setReportOpened((prev) => !prev);
  const closeReport = () => setReportOpened(false);

  useEffect(() => {
    const sync = () => {
      setFullScreen(document.fullscreenElement != null);
    };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  async function toggleFullScreen() {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await document.documentElement.requestFullscreen();
    }

    if (videoElement.current) {
      videoElement.current.click();
    }
  }

  async function terminate() {
    if (
      window.confirm(
        'Are you sure you want to terminate the streaming session?'
      )
    ) {
      try {
        await stream.terminate();
      } finally {
        window.close();
        navigate('/omniverse');
      }
    }
  }

  function reload() {
    window.location.reload();
  }

  async function startNewSession() {
    await stream.terminate();
    await streamStart.mutateAsync();
    window.location.reload();
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {!fullScreen && <OmniverseHeader />}
      <Box
        data-test-id="header"
        sx={{ display: 'flex', flexDirection: 'column', flex: '1 0 auto' }}
      >
        <Box
          sx={{
            backgroundColor: '#0047BA',
            p: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            borderTop: '1px solid #0047BA',
            height: '3rem',
            alignItems: 'center',
          }}
        >
          <IconButton
            title="Report issue"
            onClick={() => void toggleReport()}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <ReportProblemOutlinedIcon style={{ height: '2rem' }} />
          </IconButton>

          <IconButton
            title="Toggle fullscreen"
            onClick={() => void toggleFullScreen()}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {fullScreen ? (
              <FullscreenExitIcon style={{ height: '2rem' }} />
            ) : (
              <FullscreenIcon style={{ height: '2rem' }} />
            )}
          </IconButton>

          <IconButton
            title="Terminate"
            onClick={() => void terminate()}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon style={{ height: '2rem' }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: '1 0 auto',
            position: 'relative',
            boxSizing: 'border-box',
          }}
        >
          <video
            id={'stream-video'}
            ref={videoElement}
            playsInline
            muted
            autoPlay
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
            }}
          />

          <audio id={'stream-audio'} muted />

          {stream.loading && <StreamLoader />}
          {stream.error || streamStart.error ? (
            <StreamError
              disabled={streamStart.isPending}
              loading={streamStart.isPending}
              error={stream.error || streamStart.error}
              onReload={reload}
              onStartNewSession={() => void startNewSession()}
            />
          ) : null}
        </Box>
      </Box>

      <SessionReportDialog
        sessionId={sessionId}
        opened={reportOpened}
        onClose={closeReport}
      />
    </Box>
  );
}
